import type { UserProfileDto } from '../../../api/generated/types.gen';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

interface UserDataPreviewDialogProps {
    isOpen: boolean;
    onClose: () => void;
    data: UserProfileDto | null;
    isLoading: boolean;
}

/**
 * UserDataPreviewDialog
 *
 * Displays user profile data including parental approval (ELEV), school details, and professor information (PROFESOR).
 * Uses unified UserProfileDto with nested data structure.
 *
 * Refactored 2025-11-04: Simplified to use single data prop instead of separate props for each data type
 * - Now displays professor personal info (name, email, phone, code) before school details
 * - Protocol PDFs guaranteed to display from top-level protocolDeColaborare field
 *
 * @example
 * <UserDataPreviewDialog
 *   isOpen={dialogOpen}
 *   onClose={() => setDialogOpen(false)}
 *   data={userProfile}
 *   isLoading={false}
 * />
 */
export function UserDataPreviewDialog({
    isOpen,
    onClose,
    data,
    isLoading
}: UserDataPreviewDialogProps) {
    const hasAnyData = data?.parentalApproval || data?.school;

    const formatTimestamp = (timestamp?: Date | number) => {
        if (!timestamp) return '-';
        const date = typeof timestamp === 'number' ? new Date(timestamp * 1000) : new Date(timestamp);
        return date.toLocaleString('ro-RO');
    };

    const formatBoolean = (value?: boolean) => {
        if (value === undefined || value === null) return '-';
        return value ? '✓ Da' : '✗ Nu';
    };

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Previzualizare Date Utilizator</DialogTitle>
                </DialogHeader>

                {isLoading ? (
                    <div className="space-y-4 py-4">
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                        <Skeleton className="h-12 w-full" />
                    </div>
                ) : !hasAnyData ? (
                    <div className="flex justify-center items-center py-8">
                        <span className="text-small text-gray-500">Nu există date disponibile</span>
                    </div>
                ) : (
                    <div className="space-y-6">
                        {/* Parental Approval Section (ELEV) */}
                        {data?.parentalApproval && (
                            <div>
                                <h3 className="ecotic-dialog-section-title">
                                    Acord Parental
                                </h3>

                                {/* Student Information */}
                                <div className="grid grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Nume Student:</label>
                                        <p className="text-medium break-words text-gray-900">{data.parentalApproval.studentName || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Email Student:</label>
                                        <p className="text-medium break-words text-gray-900">{data.parentalApproval.studentEmail || '-'}</p>
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-small font-medium text-gray-700">Data Semnare:</label>
                                        <p className="text-medium break-words text-gray-900">{formatTimestamp(data.parentalApproval.submittedAt)}</p>
                                    </div>
                                </div>

                                {/* Consent Checkboxes */}
                                <div className="mb-4">
                                    <label className="mb-2 block text-small font-medium text-gray-700">Acorduri:</label>
                                    <div className="space-y-2 bg-gray-50 p-3 rounded-md">
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.parentApproval)}</span>
                                            <span className="text-small text-gray-700">Acord Parental</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.prinPrezentaConfirmamInscriereaMinorului)}</span>
                                            <span className="text-small text-gray-700">Confirmăm înscrierea minorului</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.ecoticPoateFolosiImagini)}</span>
                                            <span className="text-small text-gray-700">ECOTIC poate folosi imagini</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.amFostInformati)}</span>
                                            <span className="text-small text-gray-700">Am fost informați</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.prinPrezentaConfirmCaAmCititInformarea)}</span>
                                            <span className="text-small text-gray-700">Confirmăm citirea informării</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.confirmCaInCazulInCareProiectul)}</span>
                                            <span className="text-small text-gray-700">Confirmăm cazul proiectului</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.consimtPentruPastrareaDatelorPersonale)}</span>
                                            <span className="text-small text-gray-700">Consimțământ păstrare date personale</span>
                                        </div>
                                        <div className="flex items-start gap-2">
                                            <span className="text-small">{formatBoolean(data.parentalApproval.amCititSiSuntDeAcord)}</span>
                                            <span className="text-small text-gray-700">Am citit și sunt de acord</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Signature */}
                                {data.parentalApproval.signature && (
                                    <div>
                                        <label className="mb-2 block text-small font-medium text-gray-700">Semnătură:</label>
                                        <div className="border border-border rounded-md p-4 bg-gray-50 flex justify-center">
                                            <img
                                                src={data.parentalApproval.signature}
                                                alt="Semnătură părinte"
                                                className="max-w-full h-auto max-h-32"
                                                onError={(e) => {
                                                    const target = e.currentTarget;
                                                    target.style.display = 'none';
                                                    const parent = target.parentElement;
                                                    if (parent) {
                                                        parent.innerHTML = '<span class="text-small text-gray-500">Eroare la încărcarea semnăturii</span>';
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Professor Personal Information Section (PROFESOR) */}
                        {data?.school && (
                            <div>
                                <h3 className="ecotic-dialog-section-title">
                                    Informații Profesor
                                </h3>

                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Nume Complet:</label>
                                        <p className="text-medium break-words text-gray-900">{data.nume || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Email:</label>
                                        <p className="text-medium break-words text-gray-900">{data.email || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Telefon:</label>
                                        <p className="text-medium break-words text-gray-900">{data.telefon || '-'}</p>
                                    </div>
                                    <div>
                                        <label className="text-small font-medium text-gray-700">Cod Profesor:</label>
                                        <p className="text-medium break-words text-gray-900">{data.codProfesor || '-'}</p>
                                    </div>
                                </div>

                                {/* Protocol PDFs - GUARANTEED DISPLAY */}
                                {data.protocolDeColaborare && data.protocolDeColaborare.length > 0 && (
                                    <div className="mt-4">
                                        <label className="mb-2 block text-small font-medium text-gray-700">
                                            Protocol de Colaborare:
                                        </label>
                                        <div className="space-y-2 bg-gray-50 p-3 rounded-md">
                                            {data.protocolDeColaborare.map((docUrl, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => window.open(docUrl, '_blank')}
                                                    className="flex min-h-11 items-center text-small font-medium text-magenta hover:underline md:min-h-0"
                                                >
                                                    📄 Descarcă Protocol {index + 1}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Close Button */}
                        <div className="ecotic-dialog-footer">
                            <button
                                onClick={onClose}
                                className="flex h-11 items-center rounded-md border border-input bg-white px-4 text-small font-medium text-gray-700 hover:bg-gray-50 md:h-9"
                            >
                                Închide
                            </button>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
